"use client";

import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { formatBackgroundStyle } from "@/utils/styles/format";
import { FC } from "react";
import { z } from "zod";
import { handleOpenLeaddrop } from "@/store/slices/leaddrop";
import type { RootState } from "@/store/store";
import { LEAD_DROP_VARIANTS } from "../../../constants";
import { useDispatch, useSelector } from "react-redux";

const MetadataSchema = z.object({
    id: z.union([z.number(), z.string()]),
    meta_title: z.string(),
    meta_priority: z.union([z.number(), z.string()]).optional().default(0),
    // Per-pill text colour from the admin table (empty = fall back to theme default).
    meta_text_color: z.string().optional().default(""),
});

const Skills1DataSchema = z.object({
    id: z.union([z.number(), z.string()]).optional(),
    page_id: z.union([z.number(), z.string()]).optional(),
    component_id: z.union([z.number(), z.string()]).optional(),
    desktop_background_color: z.string().optional(),
    mobile_background_color: z.string().optional(),
    backvariant: z.enum(["variant_1", "variant_2", "variant_3"]).optional().default("variant_3"),
    gradient_color_1: z.string().optional(),
    gradient_color_2: z.string().optional(),
    gradient_color_3: z.string().optional(),
    gradient_color_4: z.string().optional(),
    variant: z.string().optional().default("1"),
    pre_title: z.string().optional().default(""),
    pre_title_color: z.string().optional().default("#FFFFFF"),
    title: z.string().optional().default(""),
    title_color: z.string().optional().default("#02a7b6"),
    description: z.string().optional().default(""),
    description_color: z.string().optional().default("rgba(255, 255, 255, 0.7)"),
    // Variant 2 (Figma light theme) — design variant + its editable colours.
    design_variant: z.string().optional().default("variant_1"),
    v2_pre_title_color: z.string().optional().default("#101112"),
    v2_title_color: z.string().optional().default("#9F1C34"),
    v2_description_color: z.string().optional().default("#101112"),
    v2_pill_bg_color: z.string().optional().default("#FFF3F6"),
    v2_pill_border_color: z.string().optional().default("rgba(159,28,52,0.7)"),
    v2_pill_text_color: z.string().optional().default("#000000"),
    download_brochure_text: z.string().optional().default("Download Brochure"),
    // Download Brochure button colours. Defaults match the original Figma
    // styling; an empty shadow colour renders the button without a shadow.
    button_bg_color: z.string().optional().default("#9F1C34"),
    button_text_color: z.string().optional().default("#FFFFFF"),
    button_shadow_color: z.string().optional().default("rgba(202,125,139,0.6)"),
    metadata: z.array(MetadataSchema).optional().default([]),
});

type Skills1Data = z.infer<typeof Skills1DataSchema>;
type Metadata = z.infer<typeof MetadataSchema>;

export const Skills1: FC<any> = (props) => {
    const dispatch = useDispatch();
    // Program brochure URL — passed to the DOWNLOAD-variant lead popup so it
    // auto-downloads once the lead is submitted (same source as Banner2/10).
    const brochureUrl = useSelector(
        (state: RootState) => state.globalslice?.course?.brochure || "",
    );
    const actualData = props?.data?.data || props?.data || props;
    const isMobile = useIsMobile();

    const parsedResult = Skills1DataSchema.safeParse(actualData);
    const parsedData = parsedResult.success ? parsedResult.data : null;
    
    if (!parsedData) {
        console.error("Skills1 validation failed", parsedResult.error);
        return null;
    }

    const bgColor = formatBackgroundStyle(parsedData, isMobile || false);

    const skills = parsedData.metadata.sort((a, b) => {
        const priorityA = typeof a.meta_priority === 'string' ? parseInt(a.meta_priority) : a.meta_priority;
        const priorityB = typeof b.meta_priority === 'string' ? parseInt(b.meta_priority) : b.meta_priority;
        return priorityA - priorityB;
    });

    // ── Variant 2 — Figma light theme (separate render; V1 below is untouched) ──
    if (parsedData.design_variant === "variant_2") {
        const poppins = "var(--font-poppins)";
        const googleSans = "var(--font-google-sans)";
        return (
          <section
            className={cn(
              "relative w-full overflow-hidden flex justify-center",
              // Figma V2: section vertical padding 60 (mobile + desktop)
              "px-[24px] py-[60px]",
              // Figma component reference: 80px horizontal padding from sm up; mobile keeps its 24px.
              "sm:px-[80px] sm:py-[60px]",
            )}
            style={{ backgroundColor: "#FFFFFF" }}
          >
            <div
              className={cn(
                // content row ↔ Download button gap (Figma: 72 mobile → 60 desktop)
                "flex flex-col items-center gap-[72px] sm:gap-[68px] md:gap-[64px] lg:gap-[60px] xl:gap-[60px]",
                "xs:w-[354px]",
                "sm:w-[960px]",
                "md:w-[1110px]",
                "lg:w-[1270px]",
                "xl:w-[1470px]",
              )}
            >
              {/* Content row: text column + pills */}
              <div
                className={cn(
                  "flex flex-col gap-[15px] w-full",
                  "sm:flex-row sm:items-center sm:gap-[15px]",
                )}
              >
                {/* Title + Description column */}
                <div
                  className={cn(
                    "flex flex-col gap-[24px] text-center",
                    "sm:text-left sm:w-[396px] sm:flex-shrink-0",
                    "md:w-[451px]",
                    "lg:w-[528px]",
                    "xl:w-[600px]",
                  )}
                >
                  {(parsedData.pre_title || parsedData.title) && (
                    <h2
                      className={cn(
                        "leading-[1.3]",
                        "text-[24px] tracking-[-0.24px]",
                        "sm:text-[25px]",
                        "md:text-[26px]",
                        "lg:text-[28px] lg:tracking-[-0.28px]",
                        "xl:text-[28px]",
                      )}
                      style={{ fontFamily: poppins, fontWeight: 600 }}
                    >
                      {parsedData.pre_title && (
                        <span style={{ color: parsedData.v2_pre_title_color }}>
                          {parsedData.pre_title}{" "}
                        </span>
                      )}
                      {parsedData.title && (
                        <span style={{ color: parsedData.v2_title_color }}>
                          {parsedData.title}
                        </span>
                      )}
                    </h2>
                  )}

                  {parsedData.description && (
                    <p
                      className={cn(
                        "leading-[1.65]",
                        "text-[14px]",
                        "sm:text-[15px]",
                        "md:text-[16px]",
                        "lg:text-[17px]",
                        "xl:text-[17px]",
                      )}
                      style={{
                        fontFamily: poppins,
                        fontWeight: 500,
                        color: parsedData.v2_description_color,
                      }}
                    >
                      {parsedData.description}
                    </p>
                  )}
                </div>

                {/* Skills pills */}
                {skills.length > 0 && (
                  <div
                    className={cn(
                      "flex flex-wrap items-center justify-center",
                      "gap-[8px] sm:gap-[10px] md:gap-[12px] lg:gap-[14px] xl:gap-[14px]",
                      "sm:flex-1",
                    )}
                  >
                    {skills.map((skill) => (
                      <div
                        key={skill.id}
                        className={cn(
                          "flex items-center justify-center border-solid border-[0.6px]",
                          "px-[20px] py-[10px] rounded-[30px]",
                        )}
                        style={{
                          backgroundColor: parsedData.v2_pill_bg_color,
                          borderColor: parsedData.v2_pill_border_color,
                        }}
                      >
                        <span
                          className={cn(
                            "leading-[1.4] whitespace-nowrap",
                            "text-[14px] tracking-[0.14px]",
                            "sm:text-[14px]",
                            "md:text-[15px]",
                            "lg:text-[16px] lg:tracking-[0.16px]",
                            "xl:text-[16px]",
                          )}
                          style={{
                            fontFamily: googleSans,
                            fontWeight: 500,
                            color:
                              skill.meta_text_color ||
                              parsedData.v2_pill_text_color,
                          }}
                        >
                          {skill.meta_title}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {parsedData.download_brochure_text && (
                <button
                  type="button"
                  data-cta-name="download_brochure"
                  className={cn(
                    "w-full sm:w-auto",
                    "flex items-center justify-center gap-[10px]",
                    "rounded-[8px] px-[24px] py-[16px]",
                  )}
                  style={{
                    backgroundColor: parsedData.button_bg_color,
                    // Was the static drop-shadow-[0px_10px_10px_rgba(...)] utility.
                    // Tailwind can't take a runtime colour, so the same filter is
                    // applied inline; a blank colour drops the shadow entirely.
                    filter: parsedData.button_shadow_color
                      ? `drop-shadow(0px 10px 10px ${parsedData.button_shadow_color})`
                      : undefined,
                  }}
                  onClick={() =>
                    dispatch(
                      handleOpenLeaddrop({
                        variant: LEAD_DROP_VARIANTS.DOWNLOAD,
                        downloadLink: brochureUrl,
                      }),
                    )
                  }
                >
                  <span
                    className="leading-[1.4] text-[16px] whitespace-nowrap"
                    style={{
                      fontFamily: poppins,
                      fontWeight: 600,
                      color: parsedData.button_text_color,
                    }}
                  >
                    {parsedData.download_brochure_text}
                  </span>
                  {/* Download arrow — inherits the button label colour. */}
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="shrink-0"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 4.75V19.25M12 19.25L18 13.25M12 19.25L6 13.25"
                      stroke={parsedData.button_text_color}
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )}
            </div>
          </section>
        );
    }

    return (
        <section
            className={cn(
                "relative w-full overflow-hidden flex justify-center",
                "px-[24px] py-[40px]",
                "sm:px-[60px] sm:py-[40px]",
                "md:px-[60px] md:py-[60px]",
                "lg:px-[80px] lg:py-[60px]",
                "xl:px-[140px] xl:py-[80px]"
            )}
            style={bgColor}
        >
            <div className={cn(
                "flex flex-col gap-[16px]",
                "xs:w-[354px]",
                "sm:w-[960px] sm:flex-row sm:gap-[18px]",
                "md:w-[1110px] md:gap-[20px]",
                "lg:w-[1270px] lg:gap-[24px]",
                "xl:w-[1470px] xl:gap-[24px]"
            )}>
                {/* Title and Description Column */}
                <div className={cn(
                    "flex flex-col",
                    "gap-[16px]",
                    "sm:gap-[18px] sm:w-[396px] sm:flex-shrink-0",
                    "md:gap-[20px] md:w-[451px]",
                    "lg:gap-[24px] lg:w-[528px]",
                    "xl:gap-[24px] xl:w-[600px]"
                )}>
                    {/* Title with highlighted text */}
                    {(parsedData.pre_title || parsedData.title) && (
                        <h2 
                            className={cn(
                                "leading-[1.4]",
                                "text-[24px] tracking-[0.24px]",
                                "sm:text-[20px] sm:tracking-[0.2px]",
                                "md:text-[24px] md:tracking-[0.24px]",
                                "lg:text-[28px] lg:tracking-[0.28px]",
                                "xl:text-[28px] xl:tracking-[0.28px]"
                            )}
                            style={{ 
                                fontFamily: "var(--font-google-sans)",
                                fontWeight: 600,
                                color: parsedData.pre_title_color
                            }}
                        >
                            {parsedData.pre_title && (
                                <span>{parsedData.pre_title} </span>
                            )}
                            {parsedData.title && (
                                <span 
                                    className="italic"
                                    style={{ 
                                        fontFamily: "var(--font-merriweather)",
                                        fontWeight: 800,
                                        color: parsedData.title_color
                                    }}
                                >
                                    {parsedData.title}
                                </span>
                            )}
                        </h2>
                    )}

                    {/* Description */}
                    {parsedData.description && (
                        <p 
                            className={cn(
                                "leading-[1.49]",
                                "text-[14px]",
                                "sm:text-[14px]",
                                "md:text-[14px]",
                                "lg:text-[18px]",
                                "xl:text-[18px]"
                            )}
                            style={{ 
                                fontFamily: "var(--font-google-sans)",
                                fontWeight: 600,
                                color: parsedData.description_color
                            }}
                        >
                            {parsedData.description}
                        </p>
                    )}
                </div>

                {/* Skills Pills */}
                {skills.length > 0 && (
                    <div className={cn(
                        "flex flex-wrap items-center justify-center gap-[14px]"
                    )}>
                        {skills.map((skill) => (
                            <div
                                key={skill.id}
                                className={cn(
                                    "flex items-center justify-center border-solid border-[0.6px]",
                                    "px-[20px] py-[10px]",
                                    "sm:px-[15px] sm:py-[8px]",
                                    "md:px-[16px] md:py-[8px]",
                                    "lg:px-[20px] lg:py-[10px]",
                                    "xl:px-[20px] xl:py-[10px]",
                                    "rounded-[30px]",
                                    "sm:rounded-[22.5px]",
                                    "md:rounded-[25.625px]",
                                    "lg:rounded-[30px]",
                                    "xl:rounded-[30px]"
                                )}
                                style={{
                                    backgroundColor: "rgba(86, 226, 245, 0.2)",
                                    borderColor: "rgba(255, 255, 255, 0.40)"
                                }}
                            >
                                <span
                                    className={cn(
                                        "leading-[1.4]",
                                        "text-[14px] tracking-[0.14px]",
                                        "sm:text-[12px] sm:tracking-[0.12px]",
                                        "md:text-[13px] md:tracking-[0.13px]",
                                        "lg:text-[16px] lg:tracking-[0.16px]",
                                        "xl:text-[16px] xl:tracking-[0.16px]"
                                    )}
                                    style={{
                                        fontFamily: "var(--font-google-sans)",
                                        fontWeight: 500,
                                        color: skill.meta_text_color || "#FFFFFF"
                                    }}
                                >
                                    {skill.meta_title}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};